import React from 'react';
import { Box, ClickAwayListener, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material';
import type { NavLink } from './types';

/** What the trigger — the group's icon on the collapsed rail — has to carry. */
export interface FlyoutTrigger {
  ref: (el: HTMLElement | null) => void;
  onClick: (e?: React.SyntheticEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-label': string;
}

// Long enough that sweeping the mouse down the rail does not flash a menu open
// at every icon it passes; short enough to feel like a response.
const OPEN_DELAY = 180;
// Room to cross the gap between the icon and the menu without it closing.
const CLOSE_DELAY = 200;

/**
 * A group's pages, beside its icon, when the sidebar is collapsed.
 *
 * On the collapsed rail a group is only an icon, so its pages had nowhere to
 * show: clicking went to the first page and the rest could not be reached
 * without opening the sidebar. This opens a small menu to the icon's right —
 * the group's name, then its pages, the one you are on marked — the way a
 * collapsed menu works in Ant Design and GitLab. It opens on hover after a
 * short pause, on click, and from the keyboard (Enter, Space or the right
 * arrow), and closes on leaving it, clicking elsewhere, or Escape.
 */
export function SidebarFlyout({
  label,
  links,
  activeKey,
  onPick,
  children,
}: {
  label: string;
  links: NavLink[];
  activeKey: string;
  onPick: (path: string) => void;
  children: (trigger: FlyoutTrigger) => React.ReactNode;
}) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [open, setOpen] = React.useState(false);
  // Opened from the keyboard: move focus into the menu so the arrows work.
  const [byKeyboard, setByKeyboard] = React.useState(false);
  const openTimer = React.useRef<number | undefined>(undefined);
  const closeTimer = React.useRef<number | undefined>(undefined);
  const menuId = React.useId();

  const clear = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };
  React.useEffect(() => clear, []);

  const show = (keyboard = false) => {
    clear();
    setByKeyboard(keyboard);
    setOpen(true);
  };
  const hide = (refocus = false) => {
    clear();
    setOpen(false);
    // After the menu is gone: focusing while it unmounts loses the focus.
    if (refocus) window.requestAnimationFrame(() => anchor?.focus());
  };

  const trigger: FlyoutTrigger = {
    ref: setAnchor,
    onClick: (e) => {
      // The sidebar's button also turns Enter into a click. The keydown has
      // already opened the menu for the keyboard, so that echo is ignored —
      // otherwise it toggled the menu shut and took the focus with it.
      if (e?.type === 'keydown') return;
      if (open) hide();
      else show();
    },
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
        e.preventDefault();
        show(true);
      } else if (e.key === 'Escape') {
        hide();
      }
    },
    onMouseEnter: () => {
      window.clearTimeout(closeTimer.current);
      openTimer.current = window.setTimeout(() => show(), OPEN_DELAY);
    },
    onMouseLeave: () => {
      window.clearTimeout(openTimer.current);
      closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY);
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-label': label,
  };

  return (
    <>
      {children(trigger)}
      <Popper
        open={open && Boolean(anchor)}
        anchorEl={anchor}
        placement="right-start"
        modifiers={[{ name: 'offset', options: { offset: [-4, 8] } }]}
        sx={(t) => ({ zIndex: t.zIndex.drawer + 2 })}
      >
        <ClickAwayListener onClickAway={() => hide()}>
          <Paper
            // The menu sits outside the sidebar on screen but inside it in the
            // page's structure, so a click here would also reach the collapsed
            // sidebar's "click anywhere to expand" — picking a page opened the
            // sidebar. The click stops here; the sidebar stays as it was.
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => window.clearTimeout(closeTimer.current)}
            onMouseLeave={() => { closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY); }}
            sx={(t) => ({
              minWidth: 220,
              maxWidth: 320,
              py: 0.75,
              bgcolor: t.tokens.surface.overlay,
              border: `1px solid ${t.tokens.border.subtle}`,
              borderRadius: `${t.ds.radius.md}px`,
              boxShadow: t.tokens.shadow.md,
            })}
          >
            <Typography
              id={menuId}
              sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.sidebar.sectionLabel, px: 1.75, pt: 0.5, pb: 0.75 })}
            >
              {label}
            </Typography>
            <MenuList
              dense
              autoFocusItem={byKeyboard}
              aria-labelledby={menuId}
              onKeyDown={(e) => {
                if (e.key === 'Escape' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  hide(true);
                } else if (e.key === 'Tab') {
                  hide();
                }
              }}
              sx={{ py: 0 }}
            >
              {links.map((link) => {
                const active = link.id === activeKey;
                return (
                  <MenuItem
                    key={link.id}
                    selected={active}
                    onClick={() => {
                      hide();
                      onPick(link.path);
                    }}
                    sx={(t) => ({
                      ...t.typography.body2,
                      mx: 0.75,
                      px: 1,
                      minHeight: t.ds.sizes.control.sm + 4,
                      borderRadius: `${t.ds.radius.sm}px`,
                      gap: 1,
                      color: active ? t.tokens.sidebar.itemActiveFg : t.tokens.text.primary,
                      fontWeight: active ? 600 : 500,
                      whiteSpace: 'normal',
                      '&.Mui-selected, &.Mui-selected:hover': { bgcolor: t.tokens.sidebar.itemActiveBg },
                      '&:hover': { bgcolor: t.tokens.sidebar.itemHoverBg },
                    })}
                  >
                    <Box
                      component="span"
                      aria-hidden
                      sx={(t) => ({
                        width: 5,
                        height: 5,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: active ? t.tokens.brand.primary : t.tokens.border.strong,
                      })}
                    />
                    {link.label}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
